import React, { useEffect, useRef, useState } from "react";
import type { FC } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  TableContainer,
  Paper,
  IconButton,
  Collapse,
  Grid,
  TextField,
  InputAdornment,
  TableSortLabel,
  Tooltip,
  Switch,
} from "@material-ui/core";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
// import { styled } from '@material-ui/core/styles';
import DeleteIcon from "@material-ui/icons/Delete";
import TypographyField from "src/components/ui-components/TypographyField";
import PencilAltIcon from "../../icons/PencilAlt";
import SearchIcon from "../../icons/Search";
import Label from "../shared/Label";
import { IColumn } from "./tableModel";
import { dateFormat } from "../../constants";

interface RowProps {
  row: any;
  index: number;
  childrenField: IColumn[];
  columns: IColumn[];
  onClickEdit: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onClickDelete: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  onRowClick: any;
  expandedRow: number | null;
  handleRowClick: (index: number) => void;
  handleEnabledChange?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

interface ITable {
  columns: IColumn[];
  dataList: any[];
  isLoading: boolean;
  isSearchable?: boolean;
  isExportExcel?: boolean;
  isExportCSV?: boolean;
  isImport?: boolean;
  uploadSchema?: any;
  childrenColumns?: IColumn[];
  infoText: string;
  onClickEdit?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onClickDelete?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  uploadData?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  showActionsHeader?: boolean;
  onRowClick?: any;
  handleEnabledChange?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  // children: React.ReactNode;
}
type Order = "asc" | "desc";
interface EnhancedTableProps {
  columns: IColumn[];
  childrenColumns?: IColumn[];
  onRequestSort: (event: React.MouseEvent<unknown>, property: any) => void;
  onClickEdit?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onClickDelete?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  order: Order;
  orderBy: string;
  showActionsHeader?: boolean;
  // rowCount: number;
}

const escapeRegExpMatch = (s) => {
  return s.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
};
const isExactMatch = (str, match) => {
  return new RegExp(`\\b${escapeRegExpMatch(match)}\\b`).test(str);
};

const truncateTextWithTooltip = (text: string, maxLength: number) => {
  if (text?.length > maxLength) {
    const truncatedText = text?.substring(0, maxLength) + "...";
    return (
      <Tooltip title={text}>
        <Typography noWrap>{truncatedText}</Typography>
      </Tooltip>
    );
  }
  return text;
};

const getTFLabel = (bool: Boolean): JSX.Element => {
  let text = "";
  let color: any = "success";
  if (bool) {
    text = "Yes";
    color = "success";
  } else {
    text = "No";
    color = "warning";
  }
  return <Label color={color}>{text}</Label>;
};
function descendingComparator<T>(a: T, b: T, orderBy: any) {
  const frst: any =
    typeof a[orderBy] === "string" ? a[orderBy].toLowerCase() : a[orderBy];
  const scnd: any =
    typeof b[orderBy] === "string" ? b[orderBy].toLowerCase() : b[orderBy];
  if (scnd < frst) {
    return -1;
  }
  if (scnd > frst) {
    return 1;
  }
  return 0;
}
function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const checkType = (data, dataType) => {
  if (dataType === "boolean") {
    return data ? "YES" : "NO";
  }
  if (dataType === "Date") {
    return dateFormat(data);
    // return ;
  }
  return data;
};
const Row: FC<RowProps> = (props) => {
  const {
    row,
    columns,
    childrenField,
    onClickEdit,
    onClickDelete,
    onRowClick,
    expandedRow,
    handleRowClick,
    index,
    handleEnabledChange
  } = props;
  const open = expandedRow === index;

  const handleToggleRow = (event) => {
    event.stopPropagation();
    handleRowClick(index);
    if (onRowClick) {
      onRowClick(row, !open);
    }
  };

  const getSwitchForBoolean = (column) => {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          handleEnabledChange(row);
        }}
        style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
      >
        <Switch
          checked={row[column.field]}
          onChange={(e) => {
            e.stopPropagation();
            handleEnabledChange(row);
          }}
          color="primary"
        />
      </div>
    );
  };  

  return (
    <>
      <TableRow
        hover
        onClick={handleToggleRow}
        role="checkbox"
        tabIndex={-1}
        style={{ height: "90px" }}
      >
        {childrenField && childrenField.length > 0 && (
          <TableCell padding="checkbox">
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => handleRowClick(index)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
        )}
        {columns.map((column, idx) => {
          const value = row[column.field];
          return (
            <TableCell
              align={column.align}
              key={idx.toString()}
              style={{
                overflow: column.width ? "hidden" : "visible",
                textOverflow: column.width ? "ellipsis" : "clip",
              }}
            >
              {(() => {
                if (column?.dataType === "Date" && value) {
                  return dateFormat(value);
                }
                if (column?.dataType === "boolean") {
                  if (column.allowInlineChangeForBoolean) {
                    return getSwitchForBoolean(column);
                  }
                  return getTFLabel(value);
                }
                return truncateTextWithTooltip(value, 20);
              })()}
            </TableCell>
          );
        })}
        {(onClickEdit || onClickDelete) && (
          <TableCell>
            {onClickEdit && (
              <IconButton sx={{ p: "5px" }} onClick={() => onClickEdit(row)}>
                <PencilAltIcon fontSize="small" />
              </IconButton>
            )}
            {onClickDelete && (
              <IconButton
                sx={{ p: "5px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onClickDelete(row);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </TableCell>
        )}
      </TableRow>
      {childrenField && childrenField.length > 0 && (
        <TableRow>
          <TableCell
            style={{
              paddingBottom: 0,
              paddingTop: 0,
              borderBottom: open ? "1px solid rgba(224, 224, 224, 1)" : 0,
            }}
          />
          <TableCell
            style={{
              paddingBottom: 0,
              paddingTop: 0,
              borderBottom: open ? "1px solid rgba(224, 224, 224, 1)" : 0,
            }}
            colSpan={columns.length + 1}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Grid sx={{ mb: 2, mt: 1 }} container spacing={2}>
                {childrenField.map((column, idx) => {
                  const value = row[column.field];
                  return (
                    <Grid
                      key={idx.toString()}
                      item
                      xs={4}
                      display="flex"
                      alignItems="center"
                    >
                      {(() => {
                        if (column?.dataType === "Date" && value) {
                          return (
                            <>
                              <Typography variant="subtitle2">
                                {column.headerName}:
                              </Typography>
                              <Typography
                                variant="body2"
                                style={{ marginLeft: "5px" }}
                              >
                                {dateFormat(value)}
                              </Typography>
                            </>
                          );
                        }
                        if (column?.dataType === "boolean") {
                          return (
                            <>
                              <Typography variant="subtitle2">
                                {column.headerName}:
                              </Typography>
                              <Typography style={{ marginLeft: "5px" }}>
                                {getTFLabel(value)}
                              </Typography>
                            </>
                          );
                        }
                        return (
                          <>
                            <TypographyField
                              variant="subtitle2"
                              title={`${column.headerName}: `}
                            />
                            <TypographyField
                              variant="body2"
                              style={{ marginLeft: "5px" }}
                              title={value}
                            />
                          </>
                        );
                      })()}
                    </Grid>
                  );
                })}
              </Grid>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    order,
    orderBy,
    columns,
    childrenColumns,
    onRequestSort,
    onClickEdit,
    onClickDelete,
    showActionsHeader,
  } = props;
  const createSortHandler =
    (property: any) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  return (
    <TableHead>
      <TableRow style={{ position: "sticky", top: 0, zIndex: 1 }}>
        {childrenColumns && childrenColumns.length > 0 && (
          <TableCell padding="checkbox" />
        )}
        {columns.map((headCell, i) => (
          <TableCell
            width={headCell.width}
            key={i.toString()}
            sortDirection={orderBy === headCell.headerName ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.field}
              direction={orderBy === headCell.field ? order : "asc"}
              onClick={createSortHandler(headCell.field)}
            >
              {headCell.headerName}
            </TableSortLabel>
          </TableCell>
        ))}
        {(onClickEdit || onClickDelete) && (
          <TableCell width="95px">{showActionsHeader && "Actions"}</TableCell>
        )}
      </TableRow>
    </TableHead>
  );
}
const DataTable: FC<ITable> = (props) => {
  const {
    columns = [],
    isLoading,
    isSearchable = true,
    dataList,
    childrenColumns = [],
    onClickEdit,
    onClickDelete,
    showActionsHeader = false,
    onRowClick = null,
    handleEnabledChange
  } = props;
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState("");
  const searchRef = useRef(null);
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const tableRef = useRef<HTMLTableElement>(null);
  const [expandedRow, setExpandedRow] = useState<null | number>(null);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleQueryChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const inputString = event.target.value || "";
    searchRef.current.value = inputString;

    const columnData = [...columns, ...childrenColumns].filter(
      (x) => x.isFilter === undefined || x.isFilter
    );

    const filterResult = dataList.filter((data) => {
      const result = columnData.map(({ field, dataType }) =>
        checkType(data[field], dataType)
      );
      if (inputString.startsWith('"') && inputString.endsWith('"')) {
        return isExactMatch(
          Object.values(result).join(" ").toLowerCase(),
          inputString.replace(/(^"|"$)/g, "")
        );
      }
      return Object.values(result)
        .join(" ")
        .toLowerCase()
        .includes(inputString.toLowerCase().replace(/(^"|"$)/g, ""));
    });

    setTableData(filterResult);
  };

  useEffect(() => {
    if (isSearchable && searchRef?.current.value) {
      searchRef.current.value = "";
    }
    setTableData(dataList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataList]);

  const handleRowClick = (index: number) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  return (
    <>
      <Grid alignItems="center" container justifyContent="space-between">
        {isSearchable && (
          <Box
            sx={{
              m: 2,
              maxWidth: "100%",
              alignItems: "center",
              display: "flex",
              width: 300,
            }}
          >
            <TextField
              fullWidth
              inputRef={searchRef}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchRef?.current?.value ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (searchRef.current) {
                          searchRef.current.value = "";
                        }
                        handleQueryChange({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>);
                      }}
                    >
                      ✖
                    </IconButton>
                  </InputAdornment>
                ) : null,
                style: {
                  height: 43,
                },
              }}
              onChange={handleQueryChange}
              placeholder="Search"
              value={searchRef?.current?.value || ""}
              variant="outlined"
            />
          </Box>
        )}
      </Grid>
      <TableContainer
        component={Paper}
        style={{ maxHeight: "65vh", minHeight: "30vh" }}
      >
        <Table ref={tableRef} stickyHeader>
          <>
            <EnhancedTableHead
              columns={columns}
              childrenColumns={childrenColumns}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              onClickEdit={onClickEdit}
              onClickDelete={onClickDelete}
              showActionsHeader={showActionsHeader}
            />
            <TableBody>
              {stableSort(tableData, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, idx) => (
                  <Row
                    key={idx.toString()}
                    row={row}
                    index={idx}
                    childrenField={childrenColumns}
                    columns={columns}
                    onClickEdit={onClickEdit}
                    onClickDelete={onClickDelete}
                    onRowClick={onRowClick}
                    expandedRow={expandedRow}
                    handleRowClick={handleRowClick}
                    handleEnabledChange={handleEnabledChange}
                  />
                ))}
            </TableBody>
          </>
        </Table>
        {!isLoading && tableData.length === 0 && (
          <>
            <Box
              sx={{
                height: 200,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h6" sx={{ pl: 0 }}>
                No data found!
              </Typography>
            </Box>
          </>
        )}
      </TableContainer>
      {tableData.length > 0 && (
        <TablePagination
          style={{ marginRight: 40 }}
          component="div"
          count={tableData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}
    </>
  );
};

DataTable.propTypes = {
  columns: PropTypes.array.isRequired,
  dataList: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSearchable: PropTypes.bool,
  isExportExcel: PropTypes.bool,
  isExportCSV: PropTypes.bool,
  isImport: PropTypes.bool,
  uploadSchema: PropTypes.object,
  uploadData: PropTypes.func,
  childrenColumns: PropTypes.array,
  onClickEdit: PropTypes.func,
  onClickDelete: PropTypes.func,
  handleEnabledChange: PropTypes.func
};
Row.propTypes = {
  childrenField: PropTypes.array,
  columns: PropTypes.array,
  onClickEdit: PropTypes.func,
  onClickDelete: PropTypes.func,
  onRowClick: PropTypes.func,
  row: PropTypes.any,
  handleEnabledChange: PropTypes.any
};
export default DataTable;
